/**
 * Idle/away auto-detection (W7, decision D3 — 5 min idle → away).
 *
 *   - Listens for mousemove/keydown/click/touchstart and the Page Visibility
 *     API. Resets an inactivity timer on each.
 *   - When the timer crosses the threshold AND the user has *no* manual
 *     status set, pushes `away` and remembers the auto-flip.
 *   - When activity resumes AND we previously auto-flipped, pushes
 *     `clear` (back to auto).
 *   - **Never overrides** an explicit manual status — if the user is on
 *     dnd/busy/online by choice, we leave them alone on both transitions.
 *
 * Mount once near the app root. Self-deduping: subsequent mounts under the
 * same window observe the same activity reference.
 */

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { isGuestSession } from '@/features/auth/services/guest-session'
import { usePresenceStore } from '../store/presence.store'
import { presenceApi } from '../services/presence.api'

const IDLE_THRESHOLD_MS = 5 * 60 * 1000  // 5 minutes (D3)
const POLL_INTERVAL_MS  = 30 * 1000      // 30 s — enough resolution, low overhead

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'focus',
] as const

export function useIdleAwayDetection(): void {
  // Auto-away reads/writes MY presence row, which is keyed by the per-org
  // contactPresenceId (the topic I own). Null until resolved/consented → the
  // detector stays dormant. `presenceApi.setMe` is token-derived server-side.
  const myUserId = useAuthStore((s) => s.presenceId)
  const autoSetAwayRef = useRef(false)
  const lastActivityRef = useRef(Date.now())

  useEffect(() => {
    if (!myUserId) return
    // Guest tabs opt out of user-wide presence even if a logged-in session is
    // shared from another tab via localStorage (per-tab flag, see guest-session).
    if (isGuestSession()) return
    if (typeof window === 'undefined') return

    const bumpActivity = () => {
      lastActivityRef.current = Date.now()
      // If we previously auto-flipped to away, reverting is the responsible
      // action — but only if the local view still says we're on auto:away.
      if (autoSetAwayRef.current) {
        const me = usePresenceStore.getState().byId[myUserId]
        if (me && me.manualStatus === 'away') {
          autoSetAwayRef.current = false
          presenceApi.setMe({ status: null }).then(({ presence }) => {
            usePresenceStore.getState().setOne(presence)
          }).catch(() => { /* socket / next interaction will retry */ })
        } else {
          // User manually changed status between our flip and now; respect it.
          autoSetAwayRef.current = false
        }
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Treat hidden as instantaneous idle so the timer fires sooner.
        lastActivityRef.current = Date.now() - IDLE_THRESHOLD_MS
      } else {
        bumpActivity()
      }
    }

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, bumpActivity, { passive: true }),
    )
    document.addEventListener('visibilitychange', handleVisibility)

    const interval = window.setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current
      if (idleFor < IDLE_THRESHOLD_MS) return
      if (autoSetAwayRef.current) return // already auto-away

      const me = usePresenceStore.getState().byId[myUserId]
      // Don't fight a manual status. Auto-flip only when manualStatus is null
      // and the user appears online.
      if (!me || me.manualStatus != null) return

      autoSetAwayRef.current = true
      presenceApi.setMe({ status: 'away' }).then(({ presence }) => {
        usePresenceStore.getState().setOne(presence)
      }).catch(() => {
        // Roll back the local flag if the API rejected.
        autoSetAwayRef.current = false
      })
    }, POLL_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bumpActivity))
      document.removeEventListener('visibilitychange', handleVisibility)
      window.clearInterval(interval)
    }
  }, [myUserId])
}
